import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Icon from '@/components/atoms/Icon/Icon';
import SectionWrapper from '@/components/atoms/SectionWrapper/SectionWrapper';
import Typography from '@/components/atoms/Typography/Typography';
import { cn } from '@/helpers/cn';
import { dataLayerPush } from '@/helpers/dataLayer';
import useTabDirection from '@/hooks/useTabDirection';
import { useState } from 'react';
import { FocusScope } from 'react-aria';
import Marquee from 'react-fast-marquee';
const Link = ({ url, text, testId, autoFocus }) => {
    const onClick = () => {
        dataLayerPush('brandClick', {
            card_type: 'Ticker',
            brand_name: text,
        });
    };
    return (_jsx(FocusScope, { autoFocus: autoFocus, children: _jsx(Typography, { variant: "heading-lg", className: "brand-ticker-item", as: "span", children: _jsx("a", { href: url, className: "mx-1.5 pb-0.25 text-text-headers focus-visible:!-outline-offset-6", "aria-label": `Visit ${text}`, "data-testid": `brand-link-${testId}`, onClick: onClick, children: text }) }) }));
};
const BrandTicker = ({ componentID, items = [], speed = 'normal', topPadding, bottomPadding, centreTheme, anchorNavTitle, arialabelBrandTicker, playLabel = 'Play', pauseLabel = 'Pause', }) => {
    const [isPaused, setIsPaused] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const tabDirection = useTabDirection();
    const isPlaying = !isPaused && !isHovering;
    const handleMouseEnter = () => {
        // Ignore hover on touch devices to prevent pause on tap
        if (window.matchMedia('(hover: none)').matches)
            return;
        setIsHovering(true);
    };
    const handleMouseLeave = () => {
        if (window.matchMedia('(hover: none)').matches)
            return;
        setIsHovering(false);
    };
    const handleFocus = (event) => {
        // Only trigger on keyboard focus (focus-visible)
        if (event.target?.matches(':focus-visible')) {
            setIsFocused(true);
        }
    };
    const handleBlur = (event) => {
        const relatedTarget = event.relatedTarget;
        const isStayingInComponent = relatedTarget?.closest('#brand-ticker-focus-wrapper');
        if (!isStayingInComponent) {
            setIsFocused(false);
        }
    };
    const getMarqueeSpeed = () => {
        switch (speed) {
            case 'slow':
                return 30;
            case 'fast':
                return 100;
            default:
                return 50;
        }
    };
    const firstHalf = items.slice(0, Math.ceil(items.length / 2));
    const secondHalf = items.slice(Math.ceil(items.length / 2));
    if (!items || items.length === 0) {
        return null;
    }
    return (_jsxs(SectionWrapper, { className: "relative bg-surface-primary", "data-testid": "BrandTicker-component", "data-epi-block-id": componentID, topPadding: topPadding, bottomPadding: bottomPadding, centreTheme: centreTheme, anchorNavTitle: anchorNavTitle, width: "full", children: [_jsxs("div", { "data-testid": "brand-ticker-container", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsx("div", { "aria-live": "polite", "aria-atomic": "true", className: "sr-only", children: arialabelBrandTicker || 'Brand ticker scrolling text' }), _jsx("div", { onFocus: handleFocus, onBlur: handleBlur, "data-testid": "brand-ticker-focus-wrapper", id: "brand-ticker-focus-wrapper", tabIndex: -1, className: "focus-visible:border-2", children: isFocused ? (_jsx("div", { className: "flex flex-wrap gap-2", id: "brand-ticker-list-view", "data-testid": "brand-ticker-list-view", children: items.map((item, index) => (_createElement(Link, { ...item, key: index, testId: `list-${index}`, autoFocus: tabDirection === 'backward' ? index === items.length - 1 : index === 0 }))) })) : (_jsxs(_Fragment, { children: [_jsx(Marquee, { speed: getMarqueeSpeed(), pauseOnHover: false, direction: "right", className: "mb-2.25 overflow-hidden", "data-testid": "brand-ticker-row-1", autoFill: true, play: isPlaying, pauseOnClick: false, children: firstHalf.map((item, index) => (_createElement(Link, { ...item, key: index, testId: `first-${index}` }))) }), _jsx(Marquee, { speed: getMarqueeSpeed(), pauseOnHover: false, direction: "left", className: "overflow-hidden", "data-testid": "brand-ticker-row-2", autoFill: true, play: isPlaying, pauseOnClick: false, children: secondHalf.map((item, index) => (_createElement(Link, { ...item, key: `second-${index}`, testId: `second-${index}` }))) })] })) })] }), _jsx("div", { className: "absolute right-0 bottom-0", children: _jsx("button", { className: cn('flex aspect-square w-1.75 items-center justify-center overflow-hidden bg-ui-specific-glass-dark-40', isFocused && 'hidden'), onClick: () => setIsPaused(!isPaused), "aria-label": isPaused ? playLabel : pauseLabel, title: isPaused ? playLabel : pauseLabel, "data-testid": "brand-ticker-toggle-button", "aria-hidden": isFocused ? 'true' : 'false', children: _jsx(Icon, { variant: isPaused ? 'play-circle' : 'pause', className: "h-1.5 w-1.5 text-symbol-contrast" }) }) })] }));
};
export default BrandTicker;
